import {
  ExtranetProductShortageListItemDto,
  GetExtranetProductShortageQueryDto,
} from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetExpandArrowColumn,
  ExtranetTable,
  ExtranetTableColumn,
  ExtranetTableRow,
  PharmacSearchWrapper,
  useBreakpoint,
  useDebounce,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacIcon, PharmacSearch } from '@pharmac/ui'
import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { createSearchParams, useSearchParams } from 'react-router-dom'
import { ExtranetViewHeader } from '../../components/extranet/ExtranetViewHeader'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { ProductShortageItem } from '../../components/product-shortages/ProductShortagesItem'
import { NoResultsResponse } from '../../components/responses/NoResultsResponse'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { cleanQueryParams } from '../../features/infinite-query/util/cleanQueryParams'
import { api } from '../../services/HttpService'

export const mapSearchParamsToQuery = (
  searchParams: URLSearchParams
): GetExtranetProductShortageQueryDto => {
  const parsedParams = {
    search: searchParams.get('search') ?? undefined,
  }

  return cleanQueryParams(parsedParams)
}

export const ProductShortagesView = () => {
  const { t } = useLanguage(['extranet', 'common'])
  const { minDesktop, maxMobile } = useBreakpoint()
  const { customer } = useCustomerContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const [debouncedParams, setDebouncedParams] = useDebounce(
    {} as GetExtranetProductShortageQueryDto
  )

  const [params, setParams] = useState<GetExtranetProductShortageQueryDto>(
    mapSearchParamsToQuery(searchParams)
  )

  const handleSearch = (value: string) => {
    setDebouncedParams((s) => ({ ...s, search: value }))
  }

  useEffect(() => {
    setParams(mapSearchParamsToQuery(searchParams))
  }, [searchParams])

  useEffect(() => {
    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...params,
          ...debouncedParams,
        })
      )
    )
  }, [debouncedParams])

  const getProductShortages = (
    id?: number
  ): UseQueryOptions<Array<ExtranetProductShortageListItemDto>> => ({
    queryFn: async () => await api.orders.getProductShortages(id as number, { ...params }),
    queryKey: ['__allProductShortages', customer?.id, params.search],
    enabled: typeof id === 'number',
  })

  const { data, isLoading } = useQuery(getProductShortages(customer?.id))

  const noResults = !isLoading && data?.length === 0

  return (
    <ExtranetWrapper>
      <ExtranetContainer
        full
        flexColumn
        paddingSmallHorizontal={!minDesktop}
        paddingLargeBottom
      >
        <ExtranetContainer
          full
          paddingSmallHorizontal={!minDesktop}
        >
          <ExtranetViewHeader title={t('productShortages.title')}>
            <PharmacSearchWrapper>
              <PharmacSearch
                onChange={handleSearch}
                placeholder={t('search', { ns: 'common' })}
                searchButtonContent={
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <PharmacIcon
                      icon="search"
                      size="22"
                    />
                  </div>
                }
              />
            </PharmacSearchWrapper>
          </ExtranetViewHeader>
        </ExtranetContainer>
        <ExtranetContainer
          full
          paddingSmallHorizontal={!minDesktop}
        >
          <ExtranetTable style={{ fontFamily: 'var(--font-family-secondary)', fontWeight: 300 }}>
            <ExtranetTableRow
              style={{
                backgroundColor: 'var(--extranet-light-purple)',
                border: 'none',
                fontWeight: 600,
              }}
            >
              <ExtranetTableColumn
                marginSmallLeft
                size={maxMobile ? 1.5 : 0}
              >
                {t('orderHistory.customerGroup')}
              </ExtranetTableColumn>
              <ExtranetTableColumn size={maxMobile ? 1.1 : 1}>
                {t('orderHistory.groupOrderCount')}
              </ExtranetTableColumn>
              <ExtranetExpandArrowColumn />
            </ExtranetTableRow>
            {
              <React.Fragment>
                {!noResults &&
                  data?.map((a) => (
                    <ProductShortageItem
                      key={a.groupId}
                      item={a}
                    />
                  ))}
                {noResults && params.search ? (
                  <NoResultsResponse />
                ) : (
                  noResults &&
                  !params.search && (
                    <NoResultsResponse
                      title={t('extranet:productShortages.noShortages')}
                      icon="smile"
                      description={t('extranet:productShortages.allGood')}
                    />
                  )
                )}
              </React.Fragment>
            }
          </ExtranetTable>
        </ExtranetContainer>
      </ExtranetContainer>
    </ExtranetWrapper>
  )
}
